import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Checkbox, Drawer } from 'antd';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { Button, BUTTON_STYLES, InputBar } from '~/ui';
import {
  useReportFields,
  useReportPresets,
  useCreatePreset,
  useDeletePreset,
} from '~/utils/api/reportPresets';
import { useToastError } from '~/utils/useToastError';
import { useToastSuccess } from '~/utils/useToastSuccess';
import LoaderSpinner from '~/components/Shared/LoaderSpinner/LoaderSpinner';
import styles from './ReportDrawer.module.scss';

interface ReportDrawerProps {
  open: boolean;
  onClose: () => void;
  reportType: string;
  onExport: (fields: string[], presetId?: number) => void;
  onPresetExport: (presetId: number) => void;
  extraContent?: ReactNode;
  title?: string;
  isExportDisabled?: boolean;
  extraParams?: Record<string, unknown>;
}

export const ReportDrawer = ({
  open,
  onClose,
  reportType,
  onExport,
  onPresetExport,
  extraContent,
  title,
  isExportDisabled = false,
  extraParams,
}: ReportDrawerProps) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'settings' | 'presets'>(
    'settings'
  );
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [fieldsOrder, setFieldsOrder] = useState<string[]>([]);
  const [presetName, setPresetName] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [exportingPresetId, setExportingPresetId] = useState<number | null>(
    null
  );

  const { data: fields, isLoading: fieldsLoading } =
    useReportFields(reportType);
  const { data: presets, isLoading: presetsLoading } =
    useReportPresets(reportType);
  const createPreset = useCreatePreset();
  const deletePreset = useDeletePreset(reportType);
  const toastSuccess = useToastSuccess();
  const toastError = useToastError();

  const sortedFields = useMemo(() => {
    if (!fields) return [];
    return [...fields].sort((a, b) => a.position - b.position);
  }, [fields]);

  useEffect(() => {
    if (sortedFields.length > 0) {
      const keys = sortedFields.map((f) => f.key);
      setFieldsOrder(keys);
      setSelectedFields(keys);
    }
  }, [sortedFields]);

  const orderedFields = useMemo(() => {
    if (!fields) return [];
    const fieldMap = new Map(fields.map((f) => [f.key, f]));
    return fieldsOrder
      .map((key) => fieldMap.get(key))
      .filter(Boolean) as typeof fields;
  }, [fields, fieldsOrder]);

  const allSelected = selectedFields.length === fieldsOrder.length;

  const toggleField = (key: string) => {
    setSelectedFields((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedFields([]);
    } else {
      setSelectedFields([...fieldsOrder]);
    }
  };

  const moveField = (index: number, direction: 'up' | 'down') => {
    const newOrder = [...fieldsOrder];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newOrder[index], newOrder[targetIndex]] = [
      newOrder[targetIndex],
      newOrder[index],
    ];
    setFieldsOrder(newOrder);
  };

  const handleExport = async () => {
    const exportFields = fieldsOrder.filter((key) =>
      selectedFields.includes(key)
    );
    if (exportFields.length === 0) return;
    setIsExporting(true);
    try {
      await onExport(exportFields);
      toastSuccess(t('export_completed'));
      onClose();
    } catch {
      toastError(t('xls_download_error'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleSaveAndExport = async () => {
    if (!presetName.trim()) return;

    const exportFields = fieldsOrder.filter((key) =>
      selectedFields.includes(key)
    );
    if (exportFields.length === 0) return;

    try {
      await createPreset.mutateAsync({
        report_type: reportType,
        name: presetName.trim(),
        fields: exportFields,
        ...extraParams,
      });
      toastSuccess(t('preset_saved'));
      setPresetName('');
    } catch {
      toastError(t('error_has_occurred'));
      return;
    }

    if (isExportDisabled) return;

    setIsExporting(true);
    try {
      await onExport(exportFields);
      toastSuccess(t('export_completed'));
      onClose();
    } catch {
      toastError(t('xls_download_error'));
    } finally {
      setIsExporting(false);
    }
  };

  const handlePresetExport = async (presetId: number) => {
    setIsExporting(true);
    setExportingPresetId(presetId);
    try {
      await onPresetExport(presetId);
      toastSuccess(t('export_completed'));
      onClose();
    } catch {
      toastError(t('xls_download_error'));
    } finally {
      setIsExporting(false);
      setExportingPresetId(null);
    }
  };

  const handleDeletePreset = async (id: number) => {
    try {
      await deletePreset.mutateAsync(id);
      toastSuccess(t('deleted_successfully'));
    } catch {
      toastError(t('error_has_occurred'));
    }
  };

  const handleClose = () => {
    setPresetName('');
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title={title || t('report_constructor')}
      placement="right"
      width={480}
      className={styles.drawer}
      footer={null}
    >
      <div className={styles.tabs}>
        <div
          className={cn(styles.tab, {
            [styles.tabActive]: activeTab === 'settings',
          })}
          onClick={() => setActiveTab('settings')}
        >
          {t('settings')}
        </div>
        <div
          className={cn(styles.tab, {
            [styles.tabActive]: activeTab === 'presets',
          })}
          onClick={() => setActiveTab('presets')}
        >
          {t('presets')}
        </div>
      </div>

      <div className={styles.content}>
        {activeTab === 'settings' && (
          <>
            <div className={styles.presetNameInput}>
              <InputBar
                name="preset_name"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                label={t('preset_name')}
                placeholder={t('preset_name')}
              />
            </div>

            {extraContent && (
              <div className={styles.extraContent}>{extraContent}</div>
            )}

            <div className={styles.fieldsSection}>
              <span className={styles.fieldsTitle}>
                {t('select_fields')}
              </span>

              {fieldsLoading ? (
                <div className={styles.loading}>
                  <LoaderSpinner />
                </div>
              ) : (
                <>
                  <div className={styles.selectAllField}>
                    <Checkbox
                      checked={allSelected}
                      onChange={toggleAll}
                    >
                      <span className={styles.selectAllLabel}>
                        {t('select_all')}
                      </span>
                    </Checkbox>
                  </div>

                  <div className={styles.fieldsList}>
                    {orderedFields.map((field, index) => (
                      <div key={field.key} className={styles.fieldItem}>
                        <div className={styles.fieldItemLeft}>
                          <Checkbox
                            checked={selectedFields.includes(field.key)}
                            onChange={() => toggleField(field.key)}
                          >
                            <span className={styles.fieldLabel}>
                              {field.label}
                            </span>
                          </Checkbox>
                        </div>
                        <div className={styles.fieldActions}>
                          <button
                            className={styles.moveBtn}
                            onClick={() => moveField(index, 'up')}
                            disabled={index === 0}
                          >
                            ↑
                          </button>
                          <button
                            className={styles.moveBtn}
                            onClick={() => moveField(index, 'down')}
                            disabled={index === orderedFields.length - 1}
                          >
                            ↓
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

          </>
        )}

        {activeTab === 'presets' && (
          <>
            {presetsLoading ? (
              <div className={styles.loading}>
                <LoaderSpinner />
              </div>
            ) : presets && presets.length > 0 ? (
              <div className={styles.presetsList}>
                {presets.map((preset) => (
                  <div key={preset.id} className={styles.presetItem}>
                    <span className={styles.presetName}>{preset.name}</span>
                    <div className={styles.presetActions}>
                      <Button
                        text={t('download_report')}
                        onClick={() =>
                          handlePresetExport(preset.id)
                        }
                        disabled={isExporting}
                        isLoading={
                          isExporting && exportingPresetId === preset.id
                        }
                      />
                      <button
                        className={styles.presetDeleteBtn}
                        onClick={() => handleDeletePreset(preset.id)}
                        disabled={isExporting}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyPresets}>{t('no_presets')}</div>
            )}
          </>
        )}
      </div>

      {activeTab === 'settings' && (
        <div className={styles.footer}>
          <Button
            text={t('save_and_export')}
            onClick={handleSaveAndExport}
            className={styles.footerBtn}
            disabled={
              selectedFields.length === 0 ||
              isExporting ||
              !presetName.trim()
            }
            isLoading={createPreset.isLoading}
            buttonStyle={BUTTON_STYLES.EMPTY}
          />
          <Button
            text={
              isExporting
                ? t('export_in_progress')
                : t('download_report')
            }
            onClick={handleExport}
            className={styles.footerBtn}
            disabled={
              selectedFields.length === 0 || isExporting || isExportDisabled
            }
            isLoading={isExporting}
          />
        </div>
      )}
    </Drawer>
  );
};
